import React from "react";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { isRejected } from "@reduxjs/toolkit";
import { RootState, getPayload } from "redux/store";
import { Lead } from "model/lead";
import { LoadingIndicator } from "component/shared/loading-indicator";
import { Dialog } from "component/shared/dialog";
import {
	getUser,
	getSearchAgentProfileForLead,
	getSearchAgentConversationDaysForLead,
} from "redux/selector";
import {
	fetchLeadSearchProfile,
	deleteLeadSearchProfileHistory,
	fetchLeadConversationHistory,
} from "redux/slice/search-agent";
import { SearchAgentConversationDay } from "model/search-agent-profile";
import { mixpanelAnalytics } from "shared/mixpanel-analytics";
import { canReviewLeadProfileSuggestions } from "shared/lead-profile-suggestions-permission";
import { SuggestedProfileUpdatesCard } from "./suggested-profile-updates-card";
import { SearchProfileCard } from "./search-profile-card";
import { ConversationHistoryCard } from "./conversation-history-card";
import { ConversationTranscriptDialog } from "./conversation-transcript-dialog";
import { styles } from "./style";

const mapStateToProps = (state: RootState, ownProps: { lead: Lead }) => {
	return {
		profile: getSearchAgentProfileForLead(state, ownProps.lead.id),
		conversationDays: getSearchAgentConversationDaysForLead(state, ownProps.lead.id),
		// The days selector returns an empty list before the fetch resolves, so whether it
		// has resolved is read from the slice directly.
		conversationDaysLoaded: state.searchAgent.conversationDaysByLeadId[ownProps.lead.id] !== undefined,
		searchProfileResetEnabled: getUser(state)?.permissions.searchProfileResetEnabled === true,
		canReviewProfileSuggestions: canReviewLeadProfileSuggestions(getUser(state)),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	fetchLeadSearchProfile,
	deleteLeadSearchProfileHistory,
	fetchLeadConversationHistory,
}, dispatch);

interface Props extends
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
	lead: Lead;
}

interface State {
	/** The day whose transcript is open, or undefined when no transcript is open. */
	selectedConversationDay?: SearchAgentConversationDay;
	confirmClearOpen: boolean;
	clearing: boolean;
	clearError: boolean;
	/**
	 * Tracked per card from each fetch's own outcome. The slice's `loading` and `error` are
	 * shared by every Search Agent request, so they cannot say which card is still pending or
	 * which one failed.
	 */
	profileFailed: boolean;
	conversationDaysFailed: boolean;
}

class Component extends React.Component<Props, State> {

	public readonly state: State = {
		confirmClearOpen: false,
		clearing: false,
		clearError: false,
		profileFailed: false,
		conversationDaysFailed: false,
	};

	private unmounted = false;

	public componentDidMount() {
		const leadId = this.props.lead.id;
		this.fetchFailed(this.props.fetchLeadSearchProfile({ leadId })).then((failed) => {
			if (failed) {
				this.setState({ profileFailed: true });
			}
		});
		this.fetchFailed(this.props.fetchLeadConversationHistory({ leadId })).then((failed) => {
			if (failed) {
				this.setState({ conversationDaysFailed: true });
			}
		});
	}

	public componentWillUnmount() {
		this.unmounted = true;
	}

	public render() {
		const {
			profile,
			conversationDays,
			conversationDaysLoaded,
			searchProfileResetEnabled,
			canReviewProfileSuggestions,
			classes = {},
		} = this.props;
		const { profileFailed, conversationDaysFailed } = this.state;
		// Each card's empty copy is only true once its fetch has resolved, so the spinner holds
		// until both have settled rather than flashing "no profile yet" or "no conversations
		// yet" ahead of data that is still on its way.
		const profilePending = profile === undefined && !profileFailed;
		const conversationDaysPending = !conversationDaysLoaded && !conversationDaysFailed;
		if (profilePending || conversationDaysPending) {
			return (
				<Mui.Grid container justifyContent="center">
					<Mui.Grid item>
						<LoadingIndicator />
					</Mui.Grid>
				</Mui.Grid>
			);
		}
		const { selectedConversationDay, confirmClearOpen, clearing, clearError } = this.state;
		return (
			<div className={classes.container}>
				{canReviewProfileSuggestions && (
					<SuggestedProfileUpdatesCard leadId={this.props.lead.id} />
				)}
				<SearchProfileCard
					profile={profile || undefined}
					error={profileFailed && profile === undefined}
					onClearHistory={searchProfileResetEnabled ? this.handleOpenConfirmClear : undefined}
				/>
				<ConversationHistoryCard
					conversationDays={conversationDays}
					error={conversationDaysFailed && !conversationDaysLoaded}
					onSelect={this.handleSelectConversationDay}
				/>
				{selectedConversationDay && (
					// Keyed by day so switching days remounts rather than reusing the previous
					// day's fetch bookkeeping.
					<ConversationTranscriptDialog
						key={selectedConversationDay.date.format("YYYY-MM-DD")}
						open
						onClose={this.handleCloseTranscript}
						leadId={this.props.lead.id}
						conversationDay={selectedConversationDay}
					/>
				)}
				<Dialog open={confirmClearOpen} onClose={this.handleCloseConfirmClear}>
					<Mui.DialogTitle>Clear Search Profile History</Mui.DialogTitle>
					<Mui.DialogContent>
						<Mui.DialogContentText>
							This permanently deletes this lead's AI search profile history, so the Search Agent
							starts fresh the next time they talk with it. Their conversation history is kept.
						</Mui.DialogContentText>
					</Mui.DialogContent>
					<Mui.DialogActions>
						<Mui.Button
							onClick={this.handleCloseConfirmClear}
							variant="outlined"
							color="primary"
							disabled={clearing}
						>
							Cancel
						</Mui.Button>
						<Mui.Button
							onClick={this.handleConfirmClear}
							variant="contained"
							color="primary"
							disabled={clearing}
						>
							Clear History
						</Mui.Button>
					</Mui.DialogActions>
				</Dialog>
				<Mui.Snackbar
					open={clearError}
					autoHideDuration={4000}
					onClose={() => this.setState({ clearError: false })}
				>
					<MuiAlert severity="error">
						<Mui.Typography>Failed to clear search profile history</Mui.Typography>
					</MuiAlert>
				</Mui.Snackbar>
			</div>
		);
	}

	/**
	 * Resolves true when a dispatched fetch failed. A dispatched thunk resolves with its settled
	 * action rather than throwing, so a failure is recognized by the action type. Typed loosely
	 * because the bound action creators are typed as returning the thunk, not its promise.
	 */
	private async fetchFailed(result: unknown) {
		const action = await result;
		return !this.unmounted && isRejected(action);
	}

	private handleOpenConfirmClear = () => {
		this.setState({ confirmClearOpen: true });
	};

	private handleCloseConfirmClear = () => {
		// Kept open while the delete is in flight so the outcome is not lost behind a closed dialog.
		if (!this.state.clearing) {
			this.setState({ confirmClearOpen: false });
		}
	};

	private handleConfirmClear = async () => {
		this.setState({ clearing: true });
		// A dispatched thunk resolves with its settled action rather than throwing. Only a
		// fulfilled action carries a payload, since the thunk never rejects with a value.
		const payload = getPayload(await this.props.deleteLeadSearchProfileHistory({ leadId: this.props.lead.id }));
		const failed = !payload;
		this.setState({ clearing: false, confirmClearOpen: false, clearError: failed });
	};

	private handleSelectConversationDay = (conversationDay: SearchAgentConversationDay) => {
		this.setState({ selectedConversationDay: conversationDay });
		mixpanelAnalytics.trackViewSearchAgentConversation(
			this.props.lead.id,
			conversationDay.date.format("YYYY-MM-DD")
		);
	};

	private handleCloseTranscript = () => {
		this.setState({ selectedConversationDay: undefined });
	};
}

export const SearchAgentTab = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(Component),
	styles
);
